// src/admin/components/MasterAdminHeader.js
import React, { useState, useEffect } from 'react';
import { AlertCircle, Bell } from 'lucide-react';
import { supabase } from '../../config';
import UserMenu from '../../components/common/UserMenu';

const MasterAdminHeader = ({ user, userProfile, title = "AdminHQ" }) => {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [activeMessages, setActiveMessages] = useState(0);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        // Check maintenance mode
        const { data: settingsData } = await supabase
          .from('platform_settings')
          .select('settings')
          .single();

        if (settingsData?.settings) {
          setMaintenanceMode(settingsData.settings.maintenance_mode || false);
        }

        // Count active messages
        const now = new Date().toISOString();
        const { data: messagesData } = await supabase
          .from('platform_messages')
          .select('id')
          .eq('is_active', true)
          .or(`start_date.is.null,start_date.lte.${now}`)
          .or(`end_date.is.null,end_date.gte.${now}`);

        if (messagesData) {
          setActiveMessages(messagesData.length);
        }
      } catch (error) {
        console.error('Error loading status:', error);
      }
    };

    loadStatus();
    
    // Refresh every 30 seconds
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="w-full max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Left side - Title and Status Indicators */}
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
            
            {/* Status Indicators */}
            <div className="flex items-center space-x-2">
              {maintenanceMode && (
                <div className="flex items-center px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Maintenance Mode ON
                </div>
              )}
              {activeMessages > 0 && (
                <div className="flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  <Bell className="h-3 w-3 mr-1" />
                  {activeMessages} Sitewide Message{activeMessages !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>

          {/* Right side - User menu */}
          <div className="flex items-center space-x-4">
            {user && (
              <UserMenu 
                user={user}
                userProfile={userProfile}
                userRole="master_admin"
                showQuestionsCount={true}
              />
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default MasterAdminHeader;
